/**
 * Other-cloud rows -> an Excel file the user can re-upload under the right
 * cloud. An upload checked against one cloud (say AWS) sends back the rows
 * that belong to another (Azure, GCP…) as `Other_cloud_data`; they are kept
 * out of the table, and offered here as a download.
 *
 * Port of legacy ManagePortfolio/Instances.vue downloadExcel (:180-242), with
 * the same library (SheetJS `xlsx`). `buildOtherCloudSheet` is pure (tested);
 * `downloadOtherCloudExcel` loads SheetJS only when called — it is large and
 * needed on one click, so it ships as its own chunk (vite.config.js).
 *
 * Kept from legacy: one sheet, its EIA columns and their order, the file name
 * `<CLOUD>_Portfolios_<YYYY-MM-DD>.xlsx`. Fixed:
 *  - `max iops` and `os_type` are exported too — legacy dropped them, though a
 *    re-upload needs IOPS (it is a required field);
 *  - a missing UAVG / U95 is left blank, not written as NaN;
 *  - mixed clouds are named `Other_Clouds`, not after whichever row came first;
 *  - the date is the user's local date, not UTC (legacy could be a day off);
 *  - one width per actual column (legacy set 13 widths for 11 columns).
 */
import { FIELD } from './portfolioFormRules';

/** Header -> how to read it from a server row (legacy :195-206, plus IOPS / OS). */
const COLUMNS = Object.freeze([
  ['UUID', (r) => r?.uuid ?? ''],
  ['cloud_csp', (r) => r?.cloud_csp ?? ''],
  ['region', (r) => r?.region ?? ''],
  ['instance type', (r) => r?.[FIELD.INSTANCE_TYPE] ?? ''],
  ['max cpu%', (r) => r?.[FIELD.MAX_CPU] ?? ''],
  ['max mem used', (r) => r?.[FIELD.MAX_MEM] ?? ''],
  ['max network bw', (r) => r?.[FIELD.MAX_NETWORK_BW] ?? ''],
  ['max disk bw used', (r) => r?.[FIELD.MAX_DISK_BW] ?? ''],
  ['max iops', (r) => r?.[FIELD.MAX_IOPS] ?? ''],
  ['os_type', (r) => r?.os_type ?? ''],
  ['Pricing Model', (r) => r?.pricingModel ?? ''],
  ['UAVG', (r) => numberOrBlank(r?.uavg)],
  ['U95', (r) => numberOrBlank(r?.u95)],
]);

function numberOrBlank(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? n : '';
}

/** YYYY-MM-DD in the user's own time zone. */
function localDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Everything the file needs, without touching SheetJS.
 *
 * @param {object[]} rows   the server's Other_cloud_data
 * @param {Date} [today]
 * @returns {{ fileName: string, sheetName: string, headers: string[],
 *             data: object[], widths: number[] }}
 */
export function buildOtherCloudSheet(rows, today = new Date()) {
  const clouds = [
    ...new Set(rows.map((r) => String(r?.cloud_csp ?? '').toUpperCase()).filter(Boolean)),
  ];
  const cloud = clouds.length === 1 ? clouds[0] : clouds.length > 1 ? 'Other_Clouds' : 'Cloud';
  const headers = COLUMNS.map(([header]) => header);
  return {
    fileName: `${cloud}_Portfolios_${localDate(today)}.xlsx`,
    // Excel caps sheet names at 31 characters.
    sheetName: cloud.slice(0, 31),
    headers,
    data: rows.map((row) =>
      Object.fromEntries(COLUMNS.map(([header, read]) => [header, read(row)])),
    ),
    // Legacy's widths (10–20 characters), one per column.
    widths: headers.map((h) => Math.min(Math.max(h.length + 2, 10), 20)),
  };
}

/** Build the workbook and save it. Throws if the library or the save fails. */
export async function downloadOtherCloudExcel(rows) {
  const XLSX = await import('xlsx');
  const sheet = buildOtherCloudSheet(rows);
  const ws = XLSX.utils.json_to_sheet(sheet.data, { header: sheet.headers });
  ws['!cols'] = sheet.widths.map((wch) => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheet.sheetName);
  XLSX.writeFile(wb, sheet.fileName);
}
